/**
 * authSession — session for every role.
 *
 * The API returns `{ user, accessToken, refreshToken, role }` from login. The
 * **access token is the session** (24 h, JWT with a `role` claim); the refresh
 * token is handed back in the same body. Auth endpoints are called with
 * `credentials: "include"` (so they also work if the API sets cookies) and
 * every other endpoint uses `Authorization: Bearer <accessToken>`.
 *
 * Session model (the guard depends on this):
 *   unknown       → nothing known yet (first render of a protected area)
 *   authenticated → a usable token exists for `state.role`
 *   expired       → we HAD a session; a refresh attempt failed. Never evicted —
 *                   the UI keeps rendering and the next navigation decides.
 *   anonymous     → no session for this role (never logged in / logged out)
 *
 * A failed refresh must never destroy a working session: background failures
 * (401 interceptor, pre-expiry timer) mark `expired`, and asking for one role's
 * session never touches another role's session.
 *
 * Roles and their endpoints:
 *   admin           → /api/admin/login  /api/admin/refresh  /api/admin/logout
 *   staff           → /api/staff/auth/login|refresh|logout
 *   user (learner)  → /api/auth/login|refresh|logout
 *   resource-person → /api/resource-person/auth/login|refresh|logout
 */
import { API_URL } from "../constants";
import { normalizeRole, roleFromToken, roleFromUser } from "./roles";
import { saveLearnerAvatar } from "./format";


const ROLES = {
  admin: { refresh: "/api/admin/refresh", logout: "/api/admin/logout" },
  staff: { refresh: "/api/staff/auth/refresh", logout: "/api/staff/auth/logout" },
  user: { refresh: "/api/auth/refresh", logout: "/api/auth/logout" },
  "resource-person": {
    refresh: "/api/resource-person/auth/refresh",
    logout: "/api/resource-person/auth/logout",
  },
};

/** Kept for compatibility — the cookie/lifetime is decided by the API. */
export const REFRESH_SKEW_MS = 60 * 1000;

/**
 * Routes that never need a session (marketing pages + auth entry pages).
 *
 * These used to run the same bootstrap as the dashboards, so every anonymous
 * visit produced a doomed `POST /…/refresh` →
 * `{"error":{"message":"Refresh token not found"}}`. Only the protected areas
 * (see `RequireAuth`) may ask for a session now.
 *
 * Keep in sync with the public `<Route>`s in App.js.
 */
const PUBLIC_PATHS = new Set([
  "/",
  "/contact",
  "/skills",
  "/blog",
  "/education",
  "/cart",
  "/books",
  "/journals",
  "/login",
  "/forgot-password",
  "/reset-password",
  "/register",
  "/verify-email",
  "/resource-person-registration",
  "/resource-personlogin",
  "/adminlogin",
  "/staff/login",
  "/staff/reset-password",
  // Public instructions page — exact path only; `/resource-person/dashboard`
  // and the rest of that area stay protected.
  "/resource-person",
]);

/** Prefix matches for dynamic public routes (`/blog/:slug`). */
const PUBLIC_PREFIXES = ["/blog/", "/materials/", "/books/", "/journals/"];

/** True when `pathname` belongs to a route that must never ask for a session. */
export function isPublicPath(pathname = "") {
  const clean =
    String(pathname).split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
  return PUBLIC_PATHS.has(clean) || PUBLIC_PREFIXES.some((prefix) => clean.startsWith(prefix));
}

/**
 * Set on every successful login/refresh and cleared when the session ends.
 * It is only a *hint* that this browser may hold a refresh cookie — never a
 * credential (that cookie stays httpOnly and is never readable here).
 */
const SESSION_HINT_KEY = "fillop_session_role";

function readHint() {
  try {
    return window.localStorage.getItem(SESSION_HINT_KEY);
  } catch {
    return null;
  }
}

function writeHint(role) {
  try {
    if (role) window.localStorage.setItem(SESSION_HINT_KEY, role);
    else window.localStorage.removeItem(SESSION_HINT_KEY);
  } catch {
    /* Storage unavailable (private mode) — the hint is optional. */
  }
}

/**
 * True when this browser may hold a session for some role: either a session is
 * already known, or a login happened here before (hint) — used to avoid firing
 * refreshes that are certain to fail (`Refresh token not found`).
 */
export function mayHaveSession() {
  return state.status === "authenticated" || Boolean(readHint());
}

/**
 * The remembered session for this tab (`sessionStorage`): an F5 keeps you signed
 * in, closing the tab signs you out. Tokens are never written to localStorage.
 */
const SESSION_STORE_KEY = "fillop_session";

/** Flat display keys the navbars read (never tokens). */
const IDENTITY_KEYS = ["isAdmin", "email", "uemail", "name", "image"];

/**
 * Mirror the display keys every login flow writes, so the navbars and the route
 * guards can never disagree about who is signed in.
 */
export function persistIdentity(role, { email, user } = {}) {
  try {
    localStorage.setItem("isAdmin", role === "admin" ? "true" : "false");
    const mail = email || user?.email || "";
    if (mail) {
      localStorage.setItem("email", mail);
      localStorage.setItem("uemail", mail);
    }
    const name =
      user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || mail || "";
    if (name) localStorage.setItem("name", name);
    saveLearnerAvatar(user);
  } catch {
    /* storage unavailable — the session itself still lives in memory */
  }
}

/** Remove the flat display keys (logout / definitive session loss). */
export function clearIdentity() {
  try {
    for (const key of IDENTITY_KEYS) localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** True when a login for this browser left its display keys behind. */
export function hasLocalIdentity() {
  try {
    return Boolean(
      localStorage.getItem("name") || localStorage.getItem("uemail") || localStorage.getItem("email")
    );
  } catch {
    return false;
  }
}

function writeStoredSession() {
  try {
    if (!state.token) {
      window.sessionStorage.removeItem(SESSION_STORE_KEY);
      return;
    }
    window.sessionStorage.setItem(
      SESSION_STORE_KEY,
      JSON.stringify({
        token: state.token,
        refreshToken: state.refreshToken,
        role: state.role,
        user: state.user,
      })
    );
  } catch {
    /* ignore */
  }
}

function clearStoredSession() {
  try {
    window.sessionStorage.removeItem(SESSION_STORE_KEY);
  } catch {
    /* ignore */
  }
}

let state = {
  token: null, // access token for non-auth endpoints (memory + this tab only)
  refreshToken: null, // only ever sent back to the API's refresh endpoint
  role: null,
  user: null,
  status: "unknown", // unknown | authenticated | expired | anonymous
};

/**
 * Restore the remembered session for this tab when a protected area boots.
 * An expired access token is marked expired while its refresh token is kept
 * so the session can still be renewed.
 */
export function rehydrate() {
  if (state.status === "authenticated") return state;
  let stored = null;
  try {
    stored = JSON.parse(window.sessionStorage.getItem(SESSION_STORE_KEY) || "null");
  } catch {
    stored = null;
  }
  if (!stored?.token && !stored?.refreshToken) {
    clearStoredSession();
    return null;
  }
  const tokenIsUsable = Boolean(stored.token) && !isTokenExpired(stored.token);
  state = {
    // Keep an expired access token's refresh token so a reload can renew the
    // session instead of deleting the only credential that can recover it.
    token: stored.token || null,
    refreshToken: stored.refreshToken || state.refreshToken || null,
    role: normalizeRole(stored.role) || roleFromToken(stored.token) || null,
    user: stored.user || null,
    status: tokenIsUsable ? "authenticated" : "expired",
  };
  writeHint(state.role);
  emit();
  if (tokenIsUsable) scheduleAutoRefresh(state.role, state.token);
  return state;
}


const listeners = new Set();

function emit() {
  for (const listener of listeners) listener();
}

/** Subscribe (React `useSyncExternalStore` compatible). */
export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const getSnapshot = () => state;

/** Access token used as `Authorization: Bearer` on non-auth endpoints. */
export function getToken() {
  return state.token;
}

export function getRole() {
  return state.role;
}

export function getUser() {
  return state.user;
}

/** True once a session has been established (any role). */
export function isAuthenticated() {
  return state.status === "authenticated";
}

/**
 * True when the session belongs to `role` (or when the role is still unknown on
 * an authenticated session). The route guard uses this instead of the plain
 * check so one area can never be satisfied by another area's session.
 */
export function isAuthenticatedFor(role) {
  if (state.status !== "authenticated") return false;
  if (!role || !state.role) return true;
  return state.role === role;
}

/** Decode a JWT payload without verifying the signature (client-side only). */
export function decodeJwt(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/** Recursively find an access token in an auth response body. */
export function findAccessToken(node, visited = new Set()) {
  if (!node || typeof node !== "object" || visited.has(node)) return undefined;
  visited.add(node);
  for (const key of ["accessToken", "token", "access_token", "authToken", "jwt"]) {
    if (typeof node[key] === "string" && node[key]) return node[key];
  }
  for (const key of ["data", "result", "user", "auth", "payload"]) {
    const found = findAccessToken(node[key], visited);
    if (found) return found;
  }
  return undefined;
}

/**
 * True when a JWT is missing or within `skewMs` of its `exp`.
 * Opaque (non-JWT) tokens can't be inspected, so they are treated as usable.
 */
export function isTokenExpired(token, skewMs = REFRESH_SKEW_MS) {
  if (!token) return true;
  const payload = decodeJwt(token);
  if (!payload || typeof payload.exp !== "number") return false;
  return Date.now() >= payload.exp * 1000 - skewMs;
}

/** setTimeout handle for the proactive, pre-expiry refresh. */
let refreshTimer = null;

/** Cancel any scheduled pre-expiry refresh. */
function cancelAutoRefresh() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

/**
 * Arm a silent refresh to run `skewMs` (default 60s) BEFORE the access token's
 * `exp`, so the token is renewed while it is still valid instead of waiting for
 * a 401/403. Opaque tokens (no decodable `exp`) fall back to the 401/403 path.
 */
function scheduleAutoRefresh(role, token, skewMs = REFRESH_SKEW_MS) {
  cancelAutoRefresh();
  if (!role || !token || typeof setTimeout !== "function") return;
  const payload = decodeJwt(token);
  if (!payload || typeof payload.exp !== "number") return;
  const delay = payload.exp * 1000 - Date.now() - skewMs;
  // Already inside the skew window — refreshing now would loop, so leave the
  // 401/403 fallback to cover it.
  if (delay <= 0) return;
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    refreshOnce(role).catch(() => {
      /* refresh() cleared the session on failure; the fallback stays armed */
    });
  }, Math.min(delay, 0x7fffffff));
}

/**
 * Record the session. The login response gives us `accessToken` (the session)
 * and `refreshToken` (only ever sent back to the refresh endpoint). The role is
 * taken from the token's `role` claim first — it is signed, unlike the body.
 */
export function setSession({ token, role, user, refreshToken } = {}) {
  const nextToken = token !== undefined ? token : state.token;
  state = {
    token: nextToken ?? null,
    refreshToken: refreshToken !== undefined ? refreshToken : state.refreshToken,
    role: normalizeRole(role) || roleFromToken(nextToken) || roleFromUser(user) || state.role || null,
    user: user !== undefined ? user : state.user,
    status: "authenticated",
  };
  // Remember that this browser has (had) a session, so protected routes know a
  // refresh is worth attempting after a reload.
  writeHint(state.role);
  writeStoredSession();
  emit();
  // Keep the token fresh proactively (and re-arm after every silent refresh).
  scheduleAutoRefresh(state.role, state.token);
  return state;
}

/**
 * We had a session but could not renew it (e.g. the refresh endpoint says
 * "Refresh token not found"). Keep the token/user/role so the UI keeps working
 * and the next navigation can decide — this must never look like a logout.
 */
function expireSession() {
  cancelAutoRefresh();
  state = { ...state, status: "expired" };
  emit();
}

/**
 * Forget the in-memory session. `keepHint` is used when the refresh failed for
 * another role's area: the cookie/token for the current role may still be valid,
 * so nothing that identifies this session is discarded.
 */
function forgetSession({ keepHint = false } = {}) {
  cancelAutoRefresh();
  state = { token: null, refreshToken: null, role: null, user: null, status: "anonymous" };
  clearStoredSession();
  if (!keepHint) {
    writeHint(null);
    clearIdentity();
  }
  emit();
}

/** Explicit logout: forget everything, including the remembered tab session. */
export function clearSession() {
  forgetSession();
}

/** True when there is no usable session. */
export function needsRefresh() {
  return state.status !== "authenticated";
}

/**
 * fetch() for AUTH endpoints: cookies only, no bearer token, no fallback.
 */
export async function authFetch(url, options = {}) {
  // Cookies are forced on, so a token can never act as a fallback for auth.
  return fetch(url, { ...options, credentials: "include" });
}



/**
 * POST the role's refresh endpoint.
 *
 * The login response hands the refresh token back in the body, so it is sent
 * both as a bearer token and in the JSON body while `credentials: "include"`
 * keeps working for an httpOnly cookie (whichever the API supports).
 *
 * Failure policy — this is what used to log people out:
 *   • a session already exists → mark it `expired` and KEEP the token/user/role
 *   • no session exists yet    → `anonymous` for this role only
 */
export async function refresh(role = state.role) {
  if (!role || !ROLES[role]) return null;

  const refreshToken = state.refreshToken;
  const hadSession = Boolean(state.token || state.refreshToken || state.user);

  let response;
  try {
    response = await authFetch(`${API_URL}${ROLES[role].refresh}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(refreshToken ? { Authorization: `Bearer ${refreshToken}` } : {}),
      },
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    });
  } catch (err) {
    // Network/CORS failure: nothing is proven — keep whatever we had.
    if (hadSession) expireSession();
    else forgetSession({ keepHint: true });
    throw err;
  }

  if (!response.ok) {
    if (hadSession) {
      expireSession();
    } else {
      // Definitive for THIS role only: a learner opening an admin URL must keep
      // their learner hint.
      forgetSession({ keepHint: readHint() !== role });
    }
    throw new Error(`Refresh failed (${response.status})`);
  }

  const text = await response.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = {};
    }
  }
  // The auth response carries the next access token (and often a rotated
  // refresh token) — kept in memory + this tab only.
  return setSession({
    token: findAccessToken(data),
    refreshToken: data?.refreshToken || data?.data?.refreshToken || refreshToken,
    role,
    user: data?.user ?? state.user,
  });
}

/**
 * Restore the session for `role`. Called by `RequireAuth` — i.e. only from
 * protected areas, so public pages never touch the refresh endpoint.
 *
 * Role-aware and NON-destructive:
 *   • already signed in for `role`      → return the session (no request)
 *   • signed in for another role        → `{ roleMismatch: true }`, and the
 *                                         other role's session is left intact
 *   • nothing known                     → try the remembered tab session, then
 *                                         the refresh endpoint once
 */
export async function ensureSession(role = state.role) {
  if (!role || !ROLES[role]) return null;

  // 1. Remembered session from this tab (survives a reload).
  if (state.status !== "authenticated") rehydrate();

  // 2. Already signed in?
  if (state.status === "authenticated") {
    if (!state.role || state.role === role) return state;
    // Do NOT refresh or clear: asking for one role's session must never destroy
    // the session of another role.
    return { roleMismatch: true, role: state.role };
  }

  // 3. Nothing suggests a session (no login in this browser, no hint) → a
  //    refresh would only return "Refresh token not found".
  if (!mayHaveSession()) return null;

  try {
    // Shared with the 401 retry so concurrent callers issue ONE request.
    return await refreshOnce(role);
  } catch {
    return null;
  }
}

/** POST the role's logout endpoint, then forget the local session. */
export async function logout(role = state.role) {
  try {
    if (role && ROLES[role]) {
      await authFetch(`${API_URL}${ROLES[role].logout}`, { method: "POST" });
    }
  } catch {
    /* ignore */
  } finally {
    clearSession();
  }
}

/** Auth endpoints must never be intercepted (a 401/403 there would loop). */
const AUTH_PATH_PATTERN = /(?:auth\/(?:login|refresh|logout)|\/login|\/refresh|\/logout)\b/i;

/** Statuses that mean "the access token is no longer valid". */
const AUTH_EXPIRED_STATUS = new Set([401, 403]);

/**
 * Map an API URL back to its role so a 401/403 refreshes the right session.
 * Learner endpoints are not role-prefixed, so they fall back to the active
 * role instead of assuming "user" (an admin hitting `/api/…` must not have
 * their session replaced by a learner one).
 */
function roleForUrl(url) {
  if (url.includes("/api/admin/")) return "admin";
  if (url.includes("/api/staff/")) return "staff";
  if (url.includes("/api/resource-person/")) return "resource-person";
  return state.role;
}

/** Copy any existing headers and force the current bearer token. */
function headersWithToken(input, init, token) {
  const headers = new Headers(typeof input === "string" ? undefined : input?.headers);
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => headers.set(key, value));
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return headers;
}

/** In-flight refresh shared by every concurrent 401 so only one runs. */
let refreshInFlight = null;
function refreshOnce(role) {
  if (!refreshInFlight) {
    refreshInFlight = refresh(role).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Default every API call to `credentials: "omit"` so data requests that
 * authenticate with a bearer token are never blocked by CORS. AUTH calls are
 * unaffected because `authFetch` passes `credentials: "include"` explicitly,
 * which takes precedence over this default.
 *
 * Also provides the "silent refresh": when a data request comes back 401/403
 * (the in-memory access token expired), the httpOnly refresh cookie is used to
 * mint a new access token, and the original request is retried once with it.
 * The same refresh is also scheduled ~60s before the token expires (see
 * `scheduleAutoRefresh`). Installed once, on import.
 */
export function installCookieFetch() {
  if (typeof window === "undefined" || window.__fillopApiFetch) return;
  const nativeFetch = window.fetch.bind(window);

  // Browsers can suspend timers while a tab is backgrounded or a device sleeps.
  // Re-check the token on return so a missed pre-expiry timer doesn't leave the
  // learner waiting for an API 401 before attempting refresh.
  const refreshOnResume = () => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    if (!state.role || !mayHaveSession()) return;
    if (state.status === "authenticated" && !isTokenExpired(state.token)) return;
    refreshOnce(state.role).catch(() => {
      /* Keep the expired session available for the next API/navigation retry. */
    });
  };
  window.addEventListener("focus", refreshOnResume);
  window.addEventListener("online", refreshOnResume);
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", refreshOnResume);
  }

  window.fetch = async (input, init = {}) => {
    const url = typeof input === "string" ? input : input?.url || "";
    const isApiCall = Boolean(API_URL) && url.startsWith(API_URL);
    // `init` wins, so an explicit credentials option (auth endpoints) is kept.
    const options = isApiCall ? { credentials: "omit", ...init } : init;

    const response = await nativeFetch(input, options);

    const isAuthCall = AUTH_PATH_PATTERN.test(url);
    if (!isApiCall || isAuthCall || !AUTH_EXPIRED_STATUS.has(response.status)) {
      return response;
    }

    // Anonymous callers (public pages, never logged in here) cannot be
    // refreshed — surface the 401/403 instead of firing a doomed refresh.
    if (!mayHaveSession()) return response;

    const role = roleForUrl(url) || state.role;
    const previousToken = getToken();
    try {
      // The refresh cookie re-authenticates us and returns a fresh access token.
      await refreshOnce(role);
    } catch {
      // Could not refresh — surface the original 401/403 for the caller.
      return response;
    }

    // Retry only when a *different* token was issued, so a server that does not
    // rotate the token can't cause a pointless duplicate request.
    const token = getToken();
    if (!token || token === previousToken) return response;

    // Retry once with the new bearer token (uses nativeFetch, so no loop).
    return nativeFetch(input, {
      ...options,
      headers: headersWithToken(input, options, token),
    });
  };
  window.__fillopApiFetch = true;
}

installCookieFetch();

export const authSession = {
  subscribe,
  getSnapshot,
  getToken,
  getRole,
  getUser,
  isAuthenticated,
  isAuthenticatedFor,
  isPublicPath,
  mayHaveSession,
  hasLocalIdentity,
  persistIdentity,
  clearIdentity,
  rehydrate,
  decodeJwt,
  findAccessToken,
  isTokenExpired,
  setSession,
  clearSession,
  needsRefresh,
  authFetch,
  refresh,
  ensureSession,
  logout,
  installCookieFetch,
};

export default authSession;
